import styled, { CSSObject } from '@emotion/styled'

import { Theme } from 'component-library/__styling/themes'

import { conditionalStyles } from 'component-library/__styling/conditional-styles'

import { colors, metrics, fonts } from 'component-library/__styling/settings'
import { calcRem, breakpoint, breakpointMax } from 'component-library/__styling/style-functions'
import { textCrop } from 'component-library/__styling/style-mixins'
import { withClass } from 'common/emotion'

import { IconFacebook } from 'component-library/icons/IconFacebook/IconFacebook'
import { IconTwitter } from 'component-library/icons/IconTwitter/IconTwitter'
import { IconInstagram } from 'component-library/icons/IconInstagram/IconInstagram'
import { WebLink } from 'common/navigation/web-link'

export interface StyledBioBlockProps {
    theme: Theme
}

export const StyledBioBlock = withClass('bio-block')(
    styled('div')(props => ({
        display: 'flex',
        flexWrap: 'wrap',
        background: colors.white,
        ...conditionalStyles(props.theme.kind === 'thewest', {
            marginBottom: metrics.thewest.margins.sm,
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            marginBottom: metrics.perthnow.margins.sm,
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            flexDirection: 'column',
            flexWrap: 'nowrap',

            [breakpoint('md')]: {
                flexWrap: 'wrap',
                flexDirection: 'row',
                alignItems: 'center',
                paddingRight: calcRem(16),
            },
        }),
    }))
)

export interface StyledTextProps {
    theme: Theme
}

export const StyledText = styled('div')(props => ({
    ...conditionalStyles(props.theme.kind === 'thewest', {
        flex: '1 2 100%',
        padding: `${calcRem(40, 48)}`,
        alignSelf: 'center',
        order: 0,

        [breakpoint('sm')]: {
            order: 0,
            flex: '1 1 auto',
            width: '50%',
            padding: `${calcRem(32, 33)}`,
        },
        [breakpoint('md')]: {
            padding: `${calcRem(50, 58)}`,
        },

        [breakpoint('lg')]: {
            padding: `${calcRem(45, 65)}`,
        },
    }),
    ...conditionalStyles(props.theme.kind === 'perthnow', {
        padding: calcRem(
            metrics.perthnow.margins.md,
            metrics.perthnow.margins.md,
            0,
            metrics.perthnow.margins.md
        ),
        width: '100%',
        borderBottom: `1px solid ${metrics.perthnow.margins.sm}`,
    }),
    ...conditionalStyles(props.theme.kind === 'sevennews', {
        padding: calcRem(16, 0, 22),
        marginTop: calcRem(20),
        borderTop: `1px solid ${colors.sevennews.alto}`,
        [breakpointMax('md')]: {
            marginTop: calcRem(16),
            padding: calcRem(6, 0, 0),
        },
    }),
}))

export interface StyledAuthorProps {
    theme: Theme
}

export const StyledAuthor = styled('h1')(
    {
        margin: 0,
        position: 'relative',
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            textTransform: 'uppercase',
            lineHeight: 1,
            display: 'flex',
            flexDirection: 'column',
            fontSize: calcRem(28),
            fontFamily: fonts.thewest.sansSerifCond,
            color: colors.thewest.greyGorilla,

            [breakpoint('md')]: {
                fontSize: calcRem(36),
            },

            '&::after': {
                content: `''`,
                width: '4px',
                height: '100%',
                position: 'absolute',
                left: calcRem(-13),
                top: 0,
                background: props.theme.sectionValues.primaryColor,
            },
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            borderBottom: `4px solid ${colors.perthnow.greyPyrite}`,
            paddingBottom: calcRem(metrics.perthnow.margins.sm),
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            lineHeight: 1,
            transform: `translateY(${calcRem(-metrics.perthnow.textOffset)})`,
            fontSize: calcRem(32),
            fontFamily: fonts.perthnow.heading,
            color: colors.perthnow.greyCoal,

            [breakpoint('md')]: {
                fontSize: calcRem(42),
            },
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            textAlign: 'center',
            paddingTop: calcRem(18),
            [breakpoint('sm')]: {
                textAlign: 'left',
                paddingTop: calcRem(25),
            },
        }),
    })
)

export interface StyledNameProps {
    theme: Theme
}

export const StyledName = styled('span')(
    {
        display: 'block',
        flexBasis: '100%',
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            marginBottom: calcRem(5),
            fontSize: calcRem(36),
            flex: '0 0 auto',

            ...textCrop({
                fontSize: 36,
                desiredLineHeight: 1.2,
                cropLineHeight: 1.2,
                topCrop: 10,
                bottomCrop: 8,
            }),

            [breakpoint('md')]: {
                marginBottom: calcRem(14),
            },
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            marginBottom: calcRem(16),
            fontSize: calcRem(36),
            fontFamily: fonts.sevennews.sansSerifCond,
            fontWeight: 'bold',
            flex: '0 0 auto',

            ...textCrop({
                fontSize: 36,
                desiredLineHeight: 1.2,
                cropLineHeight: 1.2,
                topCrop: 10,
                bottomCrop: 8,
            }),
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            lineHeight: 1.3,
        }),
    })
)

export interface StyledPositionProps {
    theme: Theme
}

export const StyledPosition = styled('span')(
    {
        display: 'block',
        color: colors.thewest.greyKoala,
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            textTransform: 'none',
            fontWeight: 700,
            color: colors.thewest.greyKoala,
            fontFamily: fonts.thewest.serif,
            fontSize: calcRem(18),
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            lineHeight: 1,
            fontWeight: 400,
            fontSize: calcRem(14),
            color: colors.perthnow.greyMoonstone,
            fontFamily: fonts.perthnow.sansSerif,
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            lineHeight: 1,
            fontWeight: 400,
            fontSize: calcRem(13),
            color: colors.sevennews.charade,
            fontFamily: fonts.sevennews.sansSerif,
        }),
    })
)

export interface StyledBioTextProps {
    theme: Theme
}

export const StyledBioText = styled('p')(
    {
        lineHeight: 1.375,
        margin: `${calcRem(16, 0, 21)}`,
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            color: colors.thewest.greyElephant,
            lineHeight: '1.375',
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            lineHeight: '1.875',
            margin: calcRem(16, 0, 10),
            [breakpointMax('md')]: {
                marginBottom: calcRem(24),
            },
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            color: colors.perthnow.greyCoal,
            lineHeight: '1.625',
        }),
    })
)

export interface StyledImageProps {
    theme: Theme
}

const desktopImageSize = 292
export const StyledImageContainer = styled('div')(
    {
        flex: '1 0 auto',
        width: '100%',
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            flex: '0 0 auto',
            position: 'relative',
            width: '100%',
            height: 'auto',

            [breakpoint('sm')]: {
                order: 1,
                margin: '38px 60px 38px 0',
                width: calcRem(desktopImageSize),
                height: calcRem(desktopImageSize),
                overflow: 'hidden',
                borderRadius: '50%',
            },
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            flex: '0 0 auto',
            position: 'relative',
            alignSelf: 'center',
            order: 0,
            margin: calcRem(0, 24, 0, 10),
            width: calcRem(153),
            height: calcRem(153),
            overflow: 'hidden',
            borderRadius: '50%',

            [breakpoint('md')]: {
                marginBottom: 0,
                width: calcRem(111),
                height: calcRem(111),
            },
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            flex: '1 0 auto',
            overflow: 'hidden',
            height: 0,
            paddingTop: '56.25%',
            position: 'relative',

            [breakpoint('sm')]: {
                width: '20%',
            },
        }),
    })
)

export const StyledImage = styled('img')(
    {
        width: '100%',
        height: 'auto',
        display: 'block',
    },
    props => ({
        ...conditionalStyles(props.theme.kind === 'thewest', {
            filter: 'grayscale(100%)',
            width: '100%',
            height: 'auto',

            // On desktop make the image rounded for the west
            [breakpoint('sm')]: {
                position: 'absolute',
                transform: `translate(${calcRem(-desktopImageSize / 4)})`, // Offset so the image centres.
                width: 'auto',
                height: '100%',
            },
        }),
        ...conditionalStyles(props.theme.kind === 'sevennews', {
            position: 'absolute',
            transform: `translate(${calcRem(-111 / 4)})`, // Offset so the image centres.
            width: 'auto',
            height: '100%',
        }),
        ...conditionalStyles(props.theme.kind === 'perthnow', {
            position: 'absolute',
            top: 0,
            left: 0,
        }),
    })
)

export const StyledSocialLink = styled(WebLink)({
    display: 'block',
    color: colors.sevennews.blueRibbon,
    fontSize: calcRem(14),
    fontWeight: 400,
    textDecoration: 'none',

    '&:hover': {
        textDecoration: 'underline',
    },
    '&:focus': {
        outline: `1px solid`,
        textDecoration: 'none',
    },
})

const SocialIconStyles: CSSObject = {
    width: calcRem(30),
    height: calcRem(30),
    padding: calcRem(6),
    margin: calcRem(10, 0, 10, 0),
    verticalAlign: 'middle',
    [breakpoint('sm')]: {
        margin: calcRem(12, 4, 12, 0),
    },
}

export const StyledFacebookIcon = styled(IconFacebook)(SocialIconStyles, {
    fill: colors.social.facebook,
})
export const StyledTwitterIcon = styled(IconTwitter)(SocialIconStyles, {
    fill: colors.sevennewsSocial.twitter,
})
export const StyledInstagramIcon = styled(IconInstagram)(SocialIconStyles, {
    fill: colors.social.instagram,
})
